import { useId, useMemo } from 'react';
import { contours } from 'd3-contour';
import WindBarb from './WindBarb';
import CloudIcon, { variantForOktas } from './CloudIcon';
import { WINDGRAM_HEIGHT, scaleFromWindgram } from './utils';

const CHART_HEIGHT = WINDGRAM_HEIGHT;

// Skysight's actual 16-color thermal-index palette, reverse-engineered from
// their `main-*.js` bundle. Heatmap is driven by per-altitude `ti` (thermal
// index) in m/s. Bucket lookup is "find the largest threshold ≤ value, use
// that bucket's color" (i.e. value=0.6 lands in the 0.5 bucket = dark blue).
const TI_PALETTE = [
    { thr: 0.00, color: 'rgb(173,216,230)' }, // neutral / no thermal
    { thr: 0.50, color: 'rgb(96,96,180)'   }, // weak
    { thr: 0.75, color: 'rgb(96,126,213)'  },
    { thr: 1.00, color: 'rgb(96,157,246)'  },
    { thr: 1.25, color: 'rgb(96,196,228)'  },
    { thr: 1.50, color: 'rgb(97,235,210)'  },
    { thr: 1.75, color: 'rgb(106,220,157)' },
    { thr: 2.00, color: 'rgb(116,206,104)' },
    { thr: 2.25, color: 'rgb(177,225,100)' },
    { thr: 2.50, color: 'rgb(239,244,97)'  },
    { thr: 2.75, color: 'rgb(242,205,96)'  },
    { thr: 3.00, color: 'rgb(246,167,96)'  },
    { thr: 3.25, color: 'rgb(240,131,100)' },
    { thr: 3.50, color: 'rgb(235,96,105)'  },
    { thr: 3.75, color: 'rgb(193,96,139)'  },
    { thr: 4.00, color: 'rgb(151,96,173)'  }, // extreme convection
];
const NEUTRAL_COLOR = TI_PALETTE[0].color;
const HEATMAP_OPACITY = 0.6;

// Cloud-fraction contour overlay — reverse-engineered from Skysight's bundle.
// cldfra values from our API are already in percent (0–100), confirmed by
// fixture check: top values are ~100, min nonzero ~9.52. No multiplication needed.
const CLOUD_THRESHOLDS = [0, 3, 9, 15, 25, 35, 45, 55, 67, 80, 90, 100];
const CLOUD_PALETTE = [
    'rgb(173,216,230)', // 0%  — sky-blue (matches neutral heatmap color)
    'rgb(185,185,185)', // 3%
    'rgb(178,178,178)', // 9%
    'rgb(171,171,171)', // 15%
    'rgb(164,164,164)', // 25%
    'rgb(157,157,157)', // 35%
    'rgb(150,150,150)', // 45%
    'rgb(143,143,143)', // 55%
    'rgb(140,140,140)', // 67%
    'rgb(137,137,137)', // 80%
    'rgb(134,134,134)', // 90%
    'rgb(130,130,130)', // 100%
];

const tiColor = (ti) => {
    if (!isFinite(ti) || ti < TI_PALETTE[0].thr) return NEUTRAL_COLOR;
    let picked = TI_PALETTE[0].color;
    for (const b of TI_PALETTE) {
        if (ti >= b.thr) picked = b.color;
        else break;
    }
    return picked;
};

const terrainFill = (isDark) => isDark ? '#3b2e1f' : '#c8a878';
const skyBackdrop = (isDark) => isDark ? '#0f172a' : '#e8eef4';

// Catmull-Rom → cubic Bezier path serializer. Produces smooth curves passing
// through every input point, matching Skysight's `C`-command line style for
// the thermal-top and condensation overlays.
const smoothPath = (points) => {
    if (!points || points.length === 0) return '';
    if (points.length === 1) return `M${points[0][0].toFixed(2)},${points[0][1].toFixed(2)}`;
    const [x0, y0] = points[0];
    let d = `M${x0.toFixed(2)},${y0.toFixed(2)}`;
    for (let i = 0; i < points.length - 1; i++) {
        const p0 = points[i - 1] || points[i];
        const p1 = points[i];
        const p2 = points[i + 1];
        const p3 = points[i + 2] || points[i + 1];
        const c1x = p1[0] + (p2[0] - p0[0]) / 6;
        const c1y = p1[1] + (p2[1] - p0[1]) / 6;
        const c2x = p2[0] - (p3[0] - p1[0]) / 6;
        const c2y = p2[1] - (p3[1] - p1[1]) / 6;
        d += `C${c1x.toFixed(2)},${c1y.toFixed(2)},${c2x.toFixed(2)},${c2y.toFixed(2)},${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
    }
    return d;
};

const WindgramChart = ({ data, width = 1200 }) => {
    const isDark = typeof window !== 'undefined' && localStorage.getItem('color-theme') === 'dark';
    const reactId = useId();
    const terrainClipId = `wg-terrain-${reactId.replace(/:/g, '')}`;

    const computed = useMemo(() => {
        if (!data) return null;
        const n = data.timesteps.length;
        const colW = width / n;

        // Adaptive altitude scale per Skysight's Z8(): 75/25 piecewise with split
        // at max(pblh) + 300m clamped into [base+1000, dataTop].
        const scale = scaleFromWindgram(data, CHART_HEIGHT);
        const { altToY, yToAlt, ticks } = scale;

        // Heatmap: smooth `ti` contours via d3-contour.
        // Build a 2D grid (rows = altitudes 100m..12000m, cols = timesteps), then
        // generate one contour feature per TI palette threshold. Each feature is
        // the region where ti >= threshold; stacking in ascending order yields the
        // same bucket-lookup result as a per-cell color map, but with smooth edges.
        const tiNcol = data.timesteps.length;
        const tiNrow = data.timesteps[0].column.length;
        const tiGrid = new Float32Array(tiNrow * tiNcol);
        for (let r = 0; r < tiNrow; r++) {
            for (let c = 0; c < tiNcol; c++) {
                const v = data.timesteps[c].column[r].ti;
                tiGrid[r * tiNcol + c] = isFinite(v) ? v : 0;
            }
        }
        const tiContourGen = contours()
            .size([tiNcol, tiNrow])
            .smooth(true)
            .thresholds(TI_PALETTE.map(b => b.thr));
        const tiFeatures = tiContourGen(tiGrid);
        const tiTransformX = (gx) => (gx / tiNcol) * width;
        const tiTransformY = (gy) => altToY((gy + 0.5) * 100);
        const tiFeatureToPath = (feature) => {
            const parts = [];
            for (const polygon of feature.coordinates) {
                for (const ring of polygon) {
                    parts.push(
                        'M' +
                        ring.map(([gx, gy]) => `${tiTransformX(gx).toFixed(2)},${tiTransformY(gy).toFixed(2)}`).join('L') +
                        'Z'
                    );
                }
            }
            return parts.join('');
        };
        const heatmapPaths = tiFeatures.map((feature, i) => ({
            d: tiFeatureToPath(feature),
            fill: TI_PALETTE[i].color,
        })).filter(p => p.d.length > 0);

        const ttPoints = data.thermalTopLine.map(([i, h]) => [(i + 0.5) * colW, altToY(h)]);
        const condPoints = data.condensationLine.map(([i, z]) => [(i + 0.5) * colW, altToY(z)]);
        const ttPath = smoothPath(ttPoints);
        const condPath = smoothPath(condPoints);

        const ROW_Y_FRACTIONS = [0.043, 0.189, 0.334, 0.479, 0.626, 0.773, 0.920];
        const rowAltitudes = ROW_Y_FRACTIONS.map(f => yToAlt(f * CHART_HEIGHT));

        const barbsAcc = [];
        data.timesteps.forEach((ts, t) => {
            rowAltitudes.forEach((alt, rowIdx) => {
                if (alt < data.base) return;
                const sliceIdx = Math.min(ts.column.length - 1, Math.max(0, Math.round(alt / 100) - 1));
                const slice = ts.column[sliceIdx];
                if (!slice) return;
                barbsAcc.push({
                    key: `b${t}-${rowIdx}`,
                    x: (t + 0.5) * colW,
                    y: ROW_Y_FRACTIONS[rowIdx] * CHART_HEIGHT,
                    speedMs: slice.speed,
                    dirDeg: slice.dirDeg,
                });
            });
        });

        // Cumulus icons. Both SIZE (variant) and FILL color reflect cu_oktas:
        // light wisps render bright white, denser cumulus get progressively darker
        // grays — matches Skysight's render where developed cu appear darker than
        // small fair-weather cu.
        const cloudFillForOktas = (oktas) => {
            if (oktas <= 2) return '#ffffff';
            if (oktas <= 4) return '#e2e8f0';
            if (oktas <= 6) return '#b8c0cc';
            return '#7d8694'; // dense / overcast
        };
        // Skysight's bundle only draws cumulus icons when pblh > zsfclcl (thermal
        // top must exceed the lifting condensation level for cumulus to form).
        // Size is computed as base × multiplier per their `F8()` cumulus renderer:
        //   base = clamp(chartHeight × 0.18, 14, 36)
        //   multiplier = accrain > 0 ? 1.5 : cu_oktas ≤ 2 ? 0.8 : 1
        const baseSize = Math.min(36, Math.max(14, CHART_HEIGHT * 0.18));
        const cloudsAcc = [];
        data.timesteps.forEach((ts, t) => {
            const pblh = parseFloat(ts.pblh) || 0;
            const zsfclcl = parseFloat(ts.zsfclcl) || 0;
            if (pblh <= zsfclcl) return;
            if (!ts.cu_oktas || ts.cu_oktas < 1) return;
            const alt = zsfclcl || data.condensationLine[t]?.[1];
            if (!alt) return;
            const accrain = parseFloat(ts.accrain) || 0;
            const mult = accrain > 0 ? 1.5 : ts.cu_oktas <= 2 ? 0.8 : 1;
            cloudsAcc.push({
                key: `c${t}`,
                x: (t + 0.5) * colW,
                y: altToY(alt),
                size: baseSize * mult,
                variant: variantForOktas(ts.cu_oktas),
                fill: cloudFillForOktas(ts.cu_oktas),
            });
        });

        // Cloud-fraction contour overlay.
        // Grid layout: row 0 = altitude 100m (index 0 in column array), row nrow-1 = 12000m.
        // This matches column ordering, so row r → altitude = (r+1)*100 m.
        const ncol = data.timesteps.length;
        const nrow = data.timesteps[0].column.length; // typically 120

        const rawGrid = new Float32Array(nrow * ncol);
        for (let r = 0; r < nrow; r++) {
            for (let c = 0; c < ncol; c++) {
                rawGrid[r * ncol + c] = data.timesteps[c].column[r].cldfra;
            }
        }

        // Vertical altitude-spreading (matches Skysight bundle Z8). At each
        // high-altitude row, broaden the cell's cldfra into neighboring rows in
        // the SAME time column. Radius (rows; 1 row = 100m):
        //   alt >= 6500m: ±3 rows
        //   alt >= 4000m: ±2 rows
        //   below 4000m:  no spreading
        // This makes thin high-altitude cloud layers appear puffy/wispy in the
        // chart rather than as a single 100m-tall band.
        const grid = new Float32Array(rawGrid);
        for (let r = 0; r < nrow; r++) {
            const alt = data.timesteps[0].column[r].altitude;
            const radius = alt >= 6500 ? 3 : alt >= 4000 ? 2 : 0;
            if (radius === 0) continue;
            for (let c = 0; c < ncol; c++) {
                const v = rawGrid[r * ncol + c];
                if (!isFinite(v) || v <= 0) continue;
                for (let dr = -radius; dr <= radius; dr++) {
                    const rr = r + dr;
                    if (rr < 0 || rr >= nrow) continue;
                    const idx = rr * ncol + c;
                    if (v > grid[idx]) grid[idx] = v;
                }
            }
        }

        const contourGen = contours().size([ncol, nrow]).smooth(true).thresholds(CLOUD_THRESHOLDS);
        const features = contourGen(grid);
        const transformX = (gx) => (gx / ncol) * width;
        const transformY = (gy) => altToY((gy + 0.5) * 100);
        const featureToPath = (feature) => {
            const parts = [];
            for (const polygon of feature.coordinates) {
                for (const ring of polygon) {
                    parts.push(
                        'M' +
                        ring.map(([gx, gy]) => `${transformX(gx).toFixed(2)},${transformY(gy).toFixed(2)}`).join('L') +
                        'Z'
                    );
                }
            }
            return parts.join('');
        };
        const cloudPaths = features.map((feature, i) => ({
            d: featureToPath(feature),
            fill: CLOUD_PALETTE[i] || CLOUD_PALETTE[CLOUD_PALETTE.length - 1],
            opacity: i === 0 ? 0.08 : 0.28,
        })).filter(p => p.d.length > 0);

        // Precipitation bars (accrain in mm per timestep). Rise from the terrain
        // line upward. Height uses sqrt compression so light drizzle (0.5 mm) is
        // visible but heavy rain (>5 mm) saturates instead of dominating the
        // chart. Bar width is 55 % of a column for visible gaps between cells.
        const terrainY = altToY(data.base);
        const PRECIP_MAX_HEIGHT_FRAC = 0.30;
        const PRECIP_SATURATE_MM = 5;

        const precipBars = [];
        data.timesteps.forEach((ts, t) => {
            const mm = parseFloat(ts.accrain) || 0;
            if (mm <= 0) return;
            const intensity = Math.min(1, Math.sqrt(mm / PRECIP_SATURATE_MM));
            const h = intensity * CHART_HEIGHT * PRECIP_MAX_HEIGHT_FRAC;
            const w = colW * 0.55;
            precipBars.push({
                key: `rain${t}`,
                x: (t + 0.5) * colW - w / 2,
                y: terrainY - h,
                w,
                h,
                mm,
            });
        });

        return {
            heatmapPaths,
            thermalPath: ttPath,
            condensPath: condPath,
            barbs: barbsAcc,
            clouds: cloudsAcc,
            cloudPaths,
            precipBars,
            terrainY,
            ticks,
            tickYs: ticks.map(altToY),
        };
    }, [data, width]);

    if (!data || !computed) return null;

    const thermalLineColor = isDark ? '#ffffff' : '#0f172a';
    const tickColor = isDark ? 'rgba(255,255,255,0.20)' : 'rgba(0,0,0,0.18)';
    const tickLabelColor = isDark ? 'rgba(255,255,255,0.65)' : 'rgba(0,0,0,0.55)';
    const cloudStroke = isDark ? '#0f172a' : '#475569';

    return (
        <svg
            width="100%"
            height={CHART_HEIGHT}
            viewBox={`0 0 ${width} ${CHART_HEIGHT}`}
            preserveAspectRatio="none"
            style={{ display: 'block', overflow: 'visible' }}
        >
            {/* Clip path that limits cloud rendering to the area above terrain
                (matches Skysight bundle Z8 — clouds shouldn't render below ground). */}
            <defs>
                <clipPath id={terrainClipId}>
                    <rect x={0} y={0} width={width} height={Math.max(0, computed.terrainY)} />
                </clipPath>
            </defs>

            {/* Flat sky backdrop */}
            <rect x={0} y={0} width={width} height={CHART_HEIGHT} fill={skyBackdrop(isDark)} />

            {/* Thermal-index heatmap (smooth d3-contour bands) */}
            <g opacity={HEATMAP_OPACITY}>
                {computed.heatmapPaths.map((p, i) => (
                    <path key={i} d={p.d} fill={p.fill} stroke="none" />
                ))}
            </g>

            {/* Altitude tick guides — labels overflow to the LEFT of the SVG
                (negative x). The parent <td>'s z-index toggles based on horizontal
                scroll position: when scrollLeft=0, td is above the sticky <th> so
                labels are visible; when scrolled, td drops below so labels are
                hidden behind the sticky column. */}
            {computed.ticks.map((alt, i) => (
                <g key={`tick-${i}`}>
                    <line
                        x1={0} y1={computed.tickYs[i]}
                        x2={width} y2={computed.tickYs[i]}
                        stroke={tickColor}
                        strokeDasharray="2 4"
                        strokeWidth={1}
                    />
                    <text
                        x={-6}
                        y={Math.min(CHART_HEIGHT - 4, Math.max(12, computed.tickYs[i] - 3))}
                        fontSize={13}
                        fill={tickLabelColor}
                        textAnchor="end"
                        style={{ pointerEvents: 'none' }}
                    >
                        {alt}m
                    </text>
                </g>
            ))}

            {/* Terrain */}
            <rect
                x={0}
                y={computed.terrainY}
                width={width}
                height={CHART_HEIGHT - computed.terrainY}
                fill={terrainFill(isDark)}
                opacity={0.9}
            />

            {/* Cloud-fraction contour overlay — fills only, soft layered grays.
                Skysight has cloud outlines OFF by default (gated on
                showCloudBoundary); layered semi-transparent fills produce the
                wispy appearance. Clipped to above-terrain region. */}
            <g clipPath={`url(#${terrainClipId})`}>
                {computed.cloudPaths.map((p, i) => (
                    <path key={`cf${i}`} d={p.d} fill={p.fill} fillOpacity={p.opacity} stroke="none" />
                ))}
            </g>

            {/* Precipitation bars (rise from terrain). mm label drawn inside the
                bar when it's tall enough, plus an SVG <title> for hover. */}
            <g>
                {computed.precipBars.map(b => {
                    const label = b.mm < 1 ? b.mm.toFixed(1) : Math.round(b.mm).toString();
                    const showLabel = b.h >= 16;
                    return (
                        <g key={b.key}>
                            <title>{`${b.mm.toFixed(1)} mm`}</title>
                            <rect
                                x={b.x}
                                y={b.y}
                                width={b.w}
                                height={b.h}
                                fill="rgba(56, 132, 230, 0.7)"
                                stroke="rgba(20, 70, 150, 0.85)"
                                strokeWidth={0.6}
                                rx={1}
                            />
                            {showLabel && (
                                <text
                                    x={b.x + b.w / 2}
                                    y={b.y + 11}
                                    fontSize={10}
                                    fontWeight={600}
                                    fill="#ffffff"
                                    textAnchor="middle"
                                    style={{
                                        paintOrder: 'stroke',
                                        stroke: 'rgba(20, 70, 150, 0.9)',
                                        strokeWidth: 2,
                                        pointerEvents: 'none',
                                    }}
                                >
                                    {label}
                                </text>
                            )}
                        </g>
                    );
                })}
            </g>

            {/* Condensation level (smoothed Bezier) */}
            <path
                d={computed.condensPath}
                fill="none"
                stroke="#F071B4"
                strokeWidth={2.2}
            />

            {/* Thermal-top line (smoothed Bezier) */}
            <path
                d={computed.thermalPath}
                fill="none"
                stroke={thermalLineColor}
                strokeWidth={2.2}
                style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.25))' }}
            />

            {/* Cloud icons (rendered before barbs so barbs draw on top) */}
            <g>
                {computed.clouds.map(c => (
                    <CloudIcon
                        key={c.key}
                        x={c.x}
                        y={c.y}
                        size={c.size}
                        variant={c.variant}
                        fill={c.fill}
                        stroke={cloudStroke}
                    />
                ))}
            </g>

            {/* Wind barbs (on top of cloud icons). pointer-events:none so the
                barbs don't intercept hover on precipitation bars or other
                hoverable elements that sit at the same y-coordinate. */}
            <g style={{ pointerEvents: 'none' }}>
                {computed.barbs.map(b => (
                    <WindBarb
                        key={b.key}
                        x={b.x}
                        y={b.y}
                        speedMs={b.speedMs}
                        dirDeg={b.dirDeg}
                        color={thermalLineColor}
                    />
                ))}
            </g>

            {/* Day-separator dashed verticals — drawn last so they sit on top of
                everything (heatmap, clouds, barbs) and visually separate days. */}
            {Array.from({ length: Math.floor((data.timesteps.length - 1) / 12) }, (_, i) => {
                const x = (i + 1) * 12 * (width / data.timesteps.length);
                return (
                    <line
                        key={`day-${i}`}
                        x1={x} y1={0}
                        x2={x} y2={CHART_HEIGHT}
                        stroke={isDark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.4)'}
                        strokeDasharray="3 3"
                        strokeWidth={1.2}
                    />
                );
            })}
        </svg>
    );
};

export default WindgramChart;
